import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import {
  useDepartments,
  useReassignPosition,
  useTeams,
  type PositionRow,
} from '../api/organisation-api';
import styles from './NewUnitDialog.module.css';

/**
 * F3.06 — move a seat to another unit.
 *
 * The code does not change, and the dialog says so. `NF-FIN-014` is a reference
 * people write down and email; renaming it on a move would break every mention of
 * it in exchange for a tidier prefix.
 *
 * Historical reporting keeps the original unit, which costs nothing: a report reads
 * the event log, and the move is an event.
 */
export function ReassignDialog({
  position,
  onClose,
}: {
  readonly position: PositionRow;
  readonly onClose: () => void;
}): ReactNode {
  const showToast = useToasts((state) => state.show);
  const departments = useDepartments();
  const teams = useTeams();
  const reassign = useReassignPosition();

  const [departmentId, setDepartmentId] = useState<string>(position.departmentId);
  const [teamId, setTeamId] = useState<string | null>(position.teamId);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const availableTeams = (teams.data ?? []).filter((t) => t.departmentId === departmentId);

  async function submit(): Promise<void> {
    setError(null);
    try {
      await reassign.mutateAsync({
        id: position.id,
        version: position.version,
        departmentId,
        teamId,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      showToast({ tone: 'success', message: `${position.code} moved` });
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Move ${position.code}`}
      description="The code stays the same, and past reports keep counting this seat where it used to be."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            isPending={reassign.isPending}
            disabled={departmentId === position.departmentId && teamId === position.teamId}
            onClick={() => void submit()}
          >
            Move it
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Department" isRequired>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Department"
              options={(departments.data ?? []).map((d) => ({
                value: d.id,
                label: d.name,
                meta: d.code ?? undefined,
              }))}
              value={departmentId}
              onChange={(value) => {
                setDepartmentId(value);
                // A team from the old department would put the seat in two places
                // at once, so the choice is cleared rather than silently invalid.
                setTeamId(null);
              }}
            />
          )}
        </Field>

        <Field label="Team" isOptional>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Team"
              placeholder="No team"
              options={availableTeams.map((t) => ({ value: t.id, label: t.name }))}
              value={teamId}
              onChange={setTeamId}
            />
          )}
        </Field>

        <Field label="Note" isOptional hint="Kept on the seat's history.">
          {({ inputId }) => (
            <Textarea
              id={inputId}
              value={note}
              rows={2}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Merged into the shared services team."
            />
          )}
        </Field>

        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
